import React, { forwardRef } from 'react'
import ReactPatternLock from 'react-pattern-lock';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { BounceInDownButton } from '../../animation/bounceIn';
import { RotateCcw } from 'lucide-react';
import { useChangePattern } from './api/apiSecuritysettings';
import { CircularProgress } from '@mui/material';


const schema = yup.object().shape({
    currentPattern: yup
        .array()
        .min(1, 'Draw a pattern')
        .required()
        .typeError("Connect atleast 4 dots")
        .min(4, 'Connect atleast 4 dots'),
    newPattern: yup
        .array()
        .min(1, 'Draw a pattern')
        .required()
        .typeError("Connect atleast 4 dots")
        .min(4, 'Connect atleast 4 dots')
        .test(
            'not-same-as-current',
            'New pattern must be different from the current pattern.',
            function (value) {
                return JSON.stringify(value) !== JSON.stringify(this.parent.currentPattern);
            }
        ),
    confirmNewPattern: yup
        .array()
        .min(1, 'Draw a pattern')
        .required()
        .typeError("Connect atleast 4 dots")
        .min(4, 'Connect atleast 4 dots')
})

const defaultValues = {
    currentPattern: [],
    newPattern: [],
    confirmNewPattern: []
}

const PatternLock = forwardRef((props, ref) => (
    <ReactPatternLock {...props} ref={ref} />
))

function ChangePattern() {

    const changePatternmutation = useChangePattern()
    const { control, formState, handleSubmit, reset, setError, setValue, getValues, clearErrors } = useForm({
        mode: 'onChange',
        defaultValues,
        resolver: yupResolver(schema)
    })

    const { errors } = formState

    const clearPattern = (pattern) => {
        if (getValues(pattern).length === 0) {
            return
        }
        setValue(pattern, [])
    }

    const validatePatterns = () => {
        const pattern = getValues('newPattern')
        const confirmNewPattern = getValues('confirmNewPattern')

        if (pattern.length > 0 && confirmNewPattern.length > 0) {
            if (JSON.stringify(pattern) !== JSON.stringify(confirmNewPattern)) {
                setError('confirmNewPattern', { type: 'required', message: "Patterns didn't match" })
                setTimeout(() => {
                    setValue('newPattern', [])
                    setValue('confirmNewPattern', [])
                    clearErrors('confirmNewPattern')
                }, 1000)
            }
        }
    }

    const onSubmit = async (data) => {
        let obj = {
            currentPattern: data.currentPattern.map((e) => e + 1).join(''),
            newPattern: data.newPattern.map((e) => e + 1).join(''),
            confirmNewPattern: data.confirmNewPattern.map((e) => e + 1).join(''),
        }
        let res = await changePatternmutation.mutateAsync(obj)
        if (res.status) reset(defaultValues)
    }



    return (
        
            <BounceInDownButton>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row justify-content-center">
                        <div className="col-sm-12 col-md-auto col-lg-auto col-xl-auto mb-3">
                            <div className='d-flex justify-content-between align-items-center pb-2'>
                                <h6>Current Pattern</h6>
                                <button type="button" className="btn btn-outline-link p-0" onClick={() => clearPattern("currentPattern")}>
                                    <RotateCcw size={20} />
                                </button>
                            </div>
                            <div className="pdLock">
                                <Controller
                                    name='currentPattern'
                                    control={control}
                                    render={({ ref, field }) => (
                                        <PatternLock
                                            {...field}
                                            ref={ref}
                                            width={300}
                                            size={3}
                                            path={field.value}
                                            style={{
                                                margin: "0 auto"
                                            }}
                                            onFinish={() => { }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.currentPattern && <p className="errMsg">* {errors.currentPattern.message}</p>}
                        </div>

                        <div className="col-sm-12 col-md-auto col-lg-auto col-xl-auto mb-3">
                            <div className='d-flex justify-content-between align-items-center pb-2'>
                                <h6>New Pattern</h6>
                                <button type="button" className="btn btn-outline-link p-0" onClick={() => clearPattern("newPattern")}>
                                    <RotateCcw size={20} />
                                </button>
                            </div>
                            <div className="pdLock">
                                <Controller
                                    name='newPattern'
                                    control={control}
                                    render={({ ref, field }) => (
                                        <PatternLock
                                            {...field}
                                            ref={ref}
                                            width={300}
                                            size={3}
                                            path={field.value}
                                            style={{
                                                margin: "0 auto"
                                            }}
                                            onFinish={() => { }}
                                        />
                                    )}
                                />
                            </div>
                            {errors.newPattern && (
                                <p
                                    style={{
                                        color: 'red',
                                        fontSize: '0.875rem',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                        maxWidth: '200px',
                                        marginTop: '0.25rem',
                                        marginBottom: 0
                                    }}
                                >
                                    * {errors.newPattern.message}
                                </p>
                            )}

                        </div>
                        <div className="col-sm-12 col-md-auto col-lg-auto col-xl-auto mb-3">
                            <div className='d-flex justify-content-between align-items-center pb-2'>
                                <h6>Confirm Pattern</h6>
                                <button type="button" className="btn btn-outline-link p-0" onClick={() => clearPattern("confirmNewPattern")}>
                                    <RotateCcw size={20} />
                                </button>
                            </div>
                            <div className="pdLock">
                                <Controller
                                    name='confirmNewPattern'
                                    control={control}
                                    render={({ ref, field }) => (
                                        <PatternLock
                                            {...field}
                                            ref={ref}
                                            width={300}
                                            size={3}
                                            path={field.value}
                                            style={{
                                                margin: "0 auto"
                                            }}
                                            onFinish={validatePatterns}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="d-grid crPgbtn pt-4 mt-2">
                            <button type="submit" className="btn btn-primary btn350" disabled={changePatternmutation.isLoading}>
                                {changePatternmutation.isLoading ?
                                    <CircularProgress style={{ color: "#fff" }} size={20} /> :
                                    "Change Pattern"}
                            </button>
                        </div>
                    </div>
                </form>
            </BounceInDownButton>

        
    )
}

export default ChangePattern